// Прочие view'ы: FilesPage, TelemetryPage, TasksPage, SettingsPage,
// SearchModal, DropOverlay. Портировано из design_ref/src/Views.jsx.
// (WellsTable ретайрнут — /wells теперь реальный CRUD-каталог WellsPage.)

import { createSignal, createEffect, For, Show } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import { AreaChart, series } from './Charts'
import { FILES, TASKS, SEARCH_RESULTS } from './data'
import { useAuth, LOGIN_URL } from './auth.tsx'
import type { Me } from './auth.tsx'

const FILE_FOLDERS = [
  { name: 'Берёзовское', count: 412 },
  { name: 'Приобское',   count: 893 },
  { name: 'Ванкор',      count: 287 },
  { name: 'Шаблоны',     count: 24 },
]

const FILE_FILTERS = ['Все типы', 'PDF', 'Изображения', 'CSV / LAS', 'DWG', 'Архивы']

export const FilesPage: Component = () => {
  const all = [...FILES, ...FILES.slice(0, 4)]
  return (
    <div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Файлы</h1>
          <p class="page-subtitle">2 184 файла · 47.2 ГБ из 200 ГБ</p>
        </div>
        <div class="row">
          <div class="tabs">
            <div class="tab active"><Icon name="grid" size={12} /></div>
            <div class="tab"><Icon name="list" size={12} /></div>
          </div>
          <button class="btn"><Icon name="upload" />Загрузить</button>
          <button class="btn btn-primary"><Icon name="plus" />Папка</button>
        </div>
      </div>

      <div class="row" style={{ 'margin-bottom': 'var(--sp-5)', gap: 'var(--sp-2)', 'flex-wrap': 'wrap' }}>
        <For each={FILE_FILTERS}>
          {(f, i) => (
            <span
              class={'badge ' + (i() === 0 ? 'accent' : '')}
              style={{ height: '24px', padding: '0 10px', cursor: 'pointer' }}
            >
              {f}
            </span>
          )}
        </For>
      </div>

      <div class="section-head"><h2>Папки</h2></div>
      <div class="grid-4" style={{ 'margin-bottom': 'var(--sp-7)' }}>
        <For each={FILE_FOLDERS}>
          {(f) => (
            <div
              class="card"
              style={{
                padding: 'var(--sp-4)',
                cursor: 'pointer',
                display: 'flex',
                gap: 'var(--sp-3)',
                'align-items': 'center',
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  background: 'var(--accent-soft)',
                  color: 'var(--accent)',
                  'border-radius': 'var(--radius-sm)',
                  display: 'grid',
                  'place-items': 'center',
                }}
              >
                <Icon name="folder" />
              </div>
              <div>
                <div style={{ 'font-weight': 500 }}>{f.name}</div>
                <div class="text-xs muted mono">{f.count} файлов</div>
              </div>
            </div>
          )}
        </For>
      </div>

      <div class="section-head"><h2>Недавно загружено</h2></div>
      <div class="files-grid">
        <For each={all}>
          {(f) => (
            <div class="file-card">
              <div class="file-thumb tex">
                <span>{f.kind.toUpperCase()}</span>
              </div>
              <div class="file-name" title={f.name}>{f.name}</div>
              <div class="file-meta">{f.size} · {f.date}</div>
            </div>
          )}
        </For>
      </div>
    </div>
  )
}

const TELEMETRY_CHANNELS = [
  { title: 'Глубина забоя',         val: '3247.2', unit: 'м',       color: 'var(--accent)',      seed: 11, base: 3240, vol: 4   },
  { title: 'Давление на стояке',    val: '142',    unit: 'атм',     color: 'var(--status-warn)', seed: 22, base: 142,  vol: 18  },
  { title: 'Обороты ротора',        val: '142',    unit: 'об/мин',  color: 'var(--status-info)', seed: 33, base: 142,  vol: 12  },
  { title: 'Нагрузка на долото',    val: '14.8',   unit: 'тс',      color: 'var(--accent)',      seed: 44, base: 15,   vol: 2   },
  { title: 'Расход БР',             val: '32.1',   unit: 'л/с',     color: 'var(--status-ok)',   seed: 55, base: 32,   vol: 1   },
  { title: 'Плотность БР',          val: '1.18',   unit: 'г/см³',   color: 'var(--status-ok)',   seed: 66, base: 118,  vol: 2   },
  { title: 'Температура на выходе', val: '62.4',   unit: '°C',      color: 'var(--status-warn)', seed: 77, base: 62,   vol: 3   },
  { title: 'Газопоказания',         val: '0.6',    unit: '%',       color: 'var(--status-err)',  seed: 88, base: 0.6,  vol: 0.4 },
]

export const TelemetryPage: Component = () => (
  <div class="page page-wide">
    <div class="page-header">
      <div>
        <h1 class="page-title">Телеметрия</h1>
        <p class="page-subtitle">BR-217-04 · Берёзовская 4 · live · подключено</p>
      </div>
      <div class="row">
        <span class="badge ok"><span class="dot ok" />live</span>
        <div class="tabs">
          <div class="tab">1ч</div>
          <div class="tab active">4ч</div>
          <div class="tab">24ч</div>
          <div class="tab">7д</div>
        </div>
        <button class="btn"><Icon name="upload" />Экспорт</button>
      </div>
    </div>

    <div class="telemetry-grid">
      <For each={TELEMETRY_CHANNELS}>
        {(c) => (
          <div class="chart-frame">
            <div class="chart-head">
              <div class="chart-title">{c.title}</div>
              <div class="chart-value mono">{c.val}<span class="chart-unit">{c.unit}</span></div>
            </div>
            <AreaChart data={series(c.seed, 80, c.base, c.vol)} color={c.color} />
          </div>
        )}
      </For>
    </div>
  </div>
)

export const TasksPage: Component = () => {
  const tasks = [
    ...TASKS,
    ...TASKS.map((t, i) => ({ ...t, id: 'T-1' + (79 - i), title: t.title + ' (план)', done: false })),
  ]
  return (
    <div class="page">
      <div class="page-header">
        <div>
          <h1 class="page-title">Задачи и инциденты</h1>
          <p class="page-subtitle">Лёгкий трекер — задачи живут внутри заметок и привязаны к скважинам</p>
        </div>
        <div class="row">
          <div class="tabs">
            <div class="tab active">Открытые · 9</div>
            <div class="tab">В работе · 3</div>
            <div class="tab">Закрытые</div>
          </div>
          <button class="btn btn-primary"><Icon name="plus" />Задача</button>
        </div>
      </div>

      <div class="card">
        <ul class="task-list">
          <For each={tasks}>
            {(t) => (
              <li class={'task' + (t.done ? ' done' : '')} style={{ padding: 'var(--sp-4) var(--sp-5)' }}>
                <input type="checkbox" checked={t.done} />
                <div style={{ flex: 1, 'min-width': 0 }}>
                  <div class="row between">
                    <div class="task-text" style={{ 'font-size': 'var(--fs-md)' }}>{t.title}</div>
                    {t.priority === 'high' && <span class="badge err">срочно</span>}
                  </div>
                  <div class="task-meta">
                    <span class="mono">{t.id}</span>
                    <span>·</span>
                    <span class="mono" style={{ color: 'var(--accent)' }}>{t.well}</span>
                    <span>·</span>
                    <Icon name="clock" size={11} />
                    <span>{t.due}</span>
                    <span>·</span>
                    <span>А. Соколов</span>
                  </div>
                </div>
              </li>
            )}
          </For>
        </ul>
      </div>
    </div>
  )
}

const SETTINGS_TABS: [string, string][] = [
  ['general',       'Общие'],
  ['account',       'Аккаунт'],
  ['workspace',     'Рабочее пространство'],
  ['telemetry',     'Источники данных'],
  ['notifications', 'Уведомления'],
  ['shortcuts',     'Горячие клавиши'],
  ['api',           'API и интеграции'],
]

const SETTINGS_APPEARANCE = [
  { label: 'Тёмная тема',           desc: 'Графитовый фон, янтарные акценты',         on: false },
  { label: 'Компактный режим',      desc: 'Уменьшить высоту строк и отступы',         on: true  },
  { label: 'Моноширинные значения', desc: 'Все числовые данные в JetBrains Mono',     on: true  },
]

const SETTINGS_SIDEBAR = [
  { label: 'Сворачивать сайдбар по ⌘\\',         desc: 'Скрывает список проектов, оставляя полосу с иконками', on: true },
  { label: 'Запоминать последнюю скважину',      desc: 'Открывать её при следующем входе',                     on: true },
  { label: 'Показывать «Недавние» в сайдбаре',  desc: 'Быстрый доступ к 5 последним документам',              on: true },
]

const SETTINGS_FILES = [
  { label: 'Спрашивать, куда прикрепить файл',     desc: 'Показывать оверлей с выбором проекта',         on: true  },
  { label: 'Автоматически индексировать содержимое', desc: 'Полнотекстовый поиск по PDF, DOCX, CSV',      on: true  },
  { label: 'Версионирование',                       desc: 'Сохранять предыдущие версии файла',           on: false },
]

const AccountSection: Component = () => {
  const { me, logout } = useAuth()
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const onLogout = async () => {
    setBusy(true)
    setErr(null)
    try {
      await logout()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div class="settings-section">
      <h3>Аккаунт</h3>
      <p>Профиль и доступы. Авторизация через Google.</p>

      <Show when={me.loading}>
        <div class="settings-row"><div class="settings-row-label">Загрузка…</div></div>
      </Show>

      <Show when={!me.loading && me.error}>
        <div class="settings-row" style={{ color: 'var(--status-err)' }}>
          <div class="settings-row-label">Ошибка: {String(me.error)}</div>
        </div>
      </Show>

      <Show when={!me.loading && !me.error && me() === null}>
        <div class="settings-row">
          <div>
            <div class="settings-row-label">Вы не вошли</div>
            <div class="settings-row-desc">Войдите, чтобы создавать риги и видеть свой профиль</div>
          </div>
          <a class="btn btn-primary" href={LOGIN_URL} style={{ 'text-decoration': 'none' }}>
            <Icon name="user" />
            Войти через Google
          </a>
        </div>
      </Show>

      <Show when={me() != null}>
        {(_) => {
          const u = () => me() as Me
          return (
            <>
              <div class="settings-row">
                <div style={{ display: 'flex', 'align-items': 'center', gap: 'var(--sp-3)', 'min-width': 0 }}>
                  <Show
                    when={u().picture_url}
                    fallback={
                      <div class="avatar" style={{ width: '48px', height: '48px', 'font-size': '18px' }}>
                        {(u().name?.[0] ?? u().email[0] ?? '?').toUpperCase()}
                      </div>
                    }
                  >
                    <img
                      class="avatar"
                      src={u().picture_url!}
                      alt=""
                      referrerpolicy="no-referrer"
                      style={{ width: '48px', height: '48px', 'object-fit': 'cover' }}
                    />
                  </Show>
                  <div style={{ 'min-width': 0 }}>
                    <div class="settings-row-label" style={{ 'font-weight': 600 }}>
                      {u().name ?? u().email}
                    </div>
                    <div class="settings-row-desc">{u().email}</div>
                  </div>
                </div>
                <button class="btn" onClick={onLogout} disabled={busy()}>
                  {busy() ? 'Выходим…' : 'Выйти'}
                </button>
              </div>

              <Show when={err()}>
                <div class="settings-row" style={{ color: 'var(--status-err)' }}>
                  <div class="settings-row-label">{err()}</div>
                </div>
              </Show>

              <div class="settings-row">
                <div>
                  <div class="settings-row-label">Email подтверждён</div>
                  <div class="settings-row-desc">
                    Проверка через Google ID token
                  </div>
                </div>
                <span class={'badge ' + (u().email_verified ? 'ok' : 'warn')}>
                  <span class={'dot ' + (u().email_verified ? 'ok' : 'warn')} />
                  {u().email_verified ? 'Да' : 'Нет'}
                </span>
              </div>

              <div class="settings-row">
                <div>
                  <div class="settings-row-label">Статус учётной записи</div>
                  <div class="settings-row-desc">
                    Отключённые аккаунты не могут пользоваться API
                  </div>
                </div>
                <span class={'badge ' + (u().is_active ? 'ok' : 'err')}>
                  <span class={'dot ' + (u().is_active ? 'ok' : 'err')} />
                  {u().is_active ? 'Активен' : 'Отключён'}
                </span>
              </div>

              <div class="settings-row">
                <div style={{ 'min-width': 0 }}>
                  <div class="settings-row-label">Доступы</div>
                  <div class="settings-row-desc">
                    <Show
                      when={u().permissions.length > 0}
                      fallback={'Пока пусто — администратор может выдать права в админке'}
                    >
                      <For each={u().permissions}>
                        {(p, i) => (
                          <>
                            <span class="mono">{p}</span>
                            <Show when={i() < u().permissions.length - 1}>{', '}</Show>
                          </>
                        )}
                      </For>
                    </Show>
                  </div>
                </div>
              </div>
            </>
          )
        }}
      </Show>
    </div>
  )
}

export const SettingsPage: Component = () => {
  const [tab, setTab] = createSignal('account')
  return (
    <div class="settings-layout">
      <div>
        <h1 class="page-title" style={{ 'font-size': 'var(--fs-xl)', 'margin-bottom': 'var(--sp-5)' }}>Настройки</h1>
        <div class="settings-nav">
          <For each={SETTINGS_TABS}>
            {([id, label]) => (
              <div
                class={'settings-nav-item' + (tab() === id ? ' active' : '')}
                onClick={() => setTab(id)}
              >
                {label}
              </div>
            )}
          </For>
        </div>
      </div>

      <div>
        <Show when={tab() === 'account'}>
          <AccountSection />
        </Show>

        <Show when={tab() === 'general'}>
          <div class="settings-section">
            <h3>Внешний вид</h3>
            <p>Настройки темы и плотности интерфейса</p>
            <For each={SETTINGS_APPEARANCE}>
              {(r) => (
                <div class="settings-row">
                  <div>
                    <div class="settings-row-label">{r.label}</div>
                    <div class="settings-row-desc">{r.desc}</div>
                  </div>
                  <div class={'switch' + (r.on ? ' on' : '')} />
                </div>
              )}
            </For>
          </div>

          <div class="settings-section">
            <h3>Сайдбар и навигация</h3>
            <p>Поведение боковой панели и панели проектов</p>
            <For each={SETTINGS_SIDEBAR}>
              {(r) => (
                <div class="settings-row">
                  <div>
                    <div class="settings-row-label">{r.label}</div>
                    <div class="settings-row-desc">{r.desc}</div>
                  </div>
                  <div class={'switch' + (r.on ? ' on' : '')} />
                </div>
              )}
            </For>
          </div>

          <div class="settings-section">
            <h3>Файлы</h3>
            <p>Поведение при перетаскивании</p>
            <For each={SETTINGS_FILES}>
              {(r) => (
                <div class="settings-row">
                  <div>
                    <div class="settings-row-label">{r.label}</div>
                    <div class="settings-row-desc">{r.desc}</div>
                  </div>
                  <div class={'switch' + (r.on ? ' on' : '')} />
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
    </div>
  )
}

type SearchModalProps = { visible: boolean; onClose: () => void }

export const SearchModal: Component<SearchModalProps> = (props) => {
  const [q, setQ] = createSignal('')

  // Авто-фокус при открытии модалки. Используем createEffect, потому что
  // visibility — реактивный prop, и при каждом visible→true нужен новый таймер.
  createEffect(() => {
    if (props.visible) {
      setTimeout(() => document.getElementById('search-input')?.focus(), 50)
    }
  })

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') props.onClose()
  }

  return (
    <div
      class={'modal-overlay' + (props.visible ? ' visible' : '')}
      onClick={() => props.onClose()}
      onKeyDown={onKey}
    >
      <div class="search-modal" onClick={(e) => e.stopPropagation()}>
        <div class="search-modal-input">
          <Icon name="search" size={18} />
          <input
            id="search-input"
            placeholder="Найти скважины, заметки, файлы, задачи…"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
            onKeyDown={onKey}
          />
          <span class="kbd">esc</span>
        </div>
        <div class="search-results">
          <For each={SEARCH_RESULTS}>
            {(r, i) => (
              <div class={'search-result' + (i() === 0 ? ' active' : '')}>
                <Icon name={r.kind === 'well' ? 'well' : r.kind === 'note' ? 'notes' : r.kind === 'file' ? 'file' : 'bug'} />
                <div>
                  <div class="search-result-title">{r.title}</div>
                  {/* snippet содержит <mark>; рендерим как HTML — данные локальные/моковые */}
                  <div class="search-result-snippet" innerHTML={r.snippet} />
                </div>
                <div class="search-result-kind">{r.kind}</div>
              </div>
            )}
          </For>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-4)',
            padding: 'var(--sp-3) var(--sp-5)',
            'border-top': '1px solid var(--line-soft)',
            'font-size': 'var(--fs-xs)',
            color: 'var(--text-tertiary)',
          }}
        >
          <span><span class="kbd">↑↓</span> навигация</span>
          <span><span class="kbd">↵</span> открыть</span>
          <span><span class="kbd">⌘↵</span> в новой вкладке</span>
          <div style={{ flex: 1 }} />
          <span>Поиск по 2 184 документам</span>
        </div>
      </div>
    </div>
  )
}

const DROP_FILES = [
  { name: 'GTI-log_15-04.csv', size: '2.4 МБ' },
  { name: 'core_2810.jpg',     size: '8.7 МБ' },
  { name: 'акт_смены.pdf',     size: '0.3 МБ' },
]

const DROP_ZONES: { title: string; meta: string; mono?: string; primary?: boolean }[] = [
  { title: 'Берёзовская 4', meta: '· текущая',                              mono: 'BR-217-04' },
  { title: 'Приобская 12',  meta: '',                                       mono: 'PR-009-12' },
  { title: 'Общая папка',   meta: 'не привязывать к скважине' },
  { title: '+ Создать новую скважину', meta: 'с этими файлами как начальными', primary: true },
]

type DropOverlayProps = { visible: boolean; onClose: () => void }

export const DropOverlay: Component<DropOverlayProps> = (props) => (
  <div class={'drop-overlay' + (props.visible ? ' visible' : '')} onClick={() => props.onClose()}>
    <div class="drop-card" onClick={(e) => e.stopPropagation()}>
      <div class="row between" style={{ 'margin-bottom': 'var(--sp-4)' }}>
        <div>
          <div style={{ 'font-size': 'var(--fs-lg)', 'font-weight': 600 }}>Куда прикрепить файлы?</div>
          <div class="text-sm muted" style={{ 'margin-top': '2px' }}>3 файла · 11.4 МБ</div>
        </div>
        <button class="btn-icon btn-ghost" onClick={() => props.onClose()}><Icon name="x" /></button>
      </div>

      <div class="drop-files">
        <For each={DROP_FILES}>
          {(f) => (
            <span class="drop-file-chip"><Icon name="file" size={11} />{f.name} · {f.size}</span>
          )}
        </For>
      </div>

      <div class="drop-zones">
        <For each={DROP_ZONES}>
          {(z) => (
            <button
              class="drop-zone"
              style={
                z.primary
                  ? { 'border-style': 'solid', 'border-color': 'var(--accent-line)', background: 'var(--accent-soft)' }
                  : {}
              }
            >
              <span class="drop-zone-title" style={z.primary ? { color: 'var(--accent)' } : {}}>{z.title}</span>
              <span class="drop-zone-meta">
                <Show when={z.mono}>{(m) => <><span class="mono">{m()}</span>{z.meta && ' '}</>}</Show>
                {z.meta}
              </span>
            </button>
          )}
        </For>
      </div>
    </div>
  </div>
)
